// Production backend
const API_BASE_URL = 'https://api.styloq.fashion';

export const apiService = {
  async recommendApparelWithText(imageFile, textDescription, chatUuid, price, chatHistory = {}, chatHistoryImages = {}, selectedGender = null, userPreferences = null) {
    try {
      // Validate image if provided
      if (imageFile) {
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp'];
        if (!allowedTypes.includes(imageFile.type)) {
          throw new Error('Invalid file type. Please use JPEG, PNG, or WebP format.');
        }

        // Validate file size (10MB max)
        const maxSize = 10 * 1024 * 1024; // 10MB in bytes
        if (imageFile.size > maxSize) {
          throw new Error('File size too large. Please use an image under 10MB.');
        }
      }

      // At least image or text is required
      if (!imageFile && (!textDescription || typeof textDescription !== 'string' || textDescription.trim().length === 0)) {
        throw new Error('Either an image or text description is required.');
      }

      // Validate chat_uuid
      if (!chatUuid || chatUuid.trim().length === 0) {
        throw new Error('Chat UUID is required.');
      }

      // Validate price
      if (!price || isNaN(price) || price <= 0) {
        throw new Error('Valid price is required.');
      }

      const formData = new FormData();
      if (imageFile) {
        formData.append('image', imageFile);
      }
      if (textDescription && typeof textDescription === 'string' && textDescription.trim()) {
        formData.append('text', textDescription.trim());
      }
      formData.append('chat_uuid', chatUuid.trim());
      formData.append('price', price.toString());
      
      // Add gender filter if selected
      if (selectedGender) {
        // Convert UI values to API values
        const genderValue = selectedGender === 'women' ? 'female' : selectedGender === 'men' ? 'male' : selectedGender;
        formData.append('gender', genderValue);
      }
      
      // Add chat history as JSON string
      if (chatHistory && (chatHistory.user_messages?.length > 0 || chatHistory.bot_messages?.length > 0)) {
        console.log('Adding chat history to FormData:', chatHistory);
        formData.append('chat_history', JSON.stringify(chatHistory));
      }
      
      // Add user preferences if provided
      if (userPreferences) {
        console.log('Adding user preferences to FormData:', userPreferences);
        formData.append('preferences', JSON.stringify(userPreferences));
      }
      
      // Add chat history images as separate fields
      if (chatHistoryImages && Object.keys(chatHistoryImages).length > 0) {
        console.log('Adding chat history images:', Object.keys(chatHistoryImages));
        Object.entries(chatHistoryImages).forEach(([key, imageData]) => {
          // Convert base64 to blob for FormData
          const base64Data = imageData.split(',')[1];
          const mimeType = imageData.split(';')[0].split(':')[1];
          const byteCharacters = atob(base64Data);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const blob = new Blob([byteArray], { type: mimeType });
          formData.append(key, blob, `${key}.jpg`);
        });
      }

      // Debug FormData contents
      console.log('FormData contents:');
      for (let [key, value] of formData.entries()) {
        console.log(`${key}:`, typeof value === 'string' ? value : 'File object');
      }

      const response = await fetch(`${API_BASE_URL}/recommend-apparel-with-text`, {
        method: 'POST',
        body: formData, // Don't set Content-Type header - browser handles it
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('API Error:', error);
      throw error;
    }
  }
};